import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { CloseSmallIcon } from '#/shared/ui/icons'

interface Recipient {
  id: string
  label: string
}

interface RecipientsFieldProps {
  recipients: Recipient[]
  onAdd?: (value: string) => void
  onRemove?: (id: string) => void
  placeholder?: string
  submitKeys?: string[]
  removeAriaLabel?: (label: string) => string
  className?: string
  inputClassName?: string
  chipClassName?: string
}

function RecipientChip({
  recipient,
  onRemove,
  removeAriaLabel,
  className,
}: {
  recipient: Recipient
  onRemove?: (id: string) => void
  removeAriaLabel?: (label: string) => string
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <span
      className={cn(
        'flex h-5 shrink-0 items-center gap-1.25 rounded-full border border-gray-200 bg-gray-50 px-2.5 text-xs text-gray-500',
        className,
      )}
    >
      {recipient.label}
      {onRemove && (
        <button
          type="button"
          aria-label={
            removeAriaLabel?.(recipient.label) ??
            t('recipients.remove', { label: recipient.label })
          }
          onClick={() => onRemove(recipient.id)}
          className="flex cursor-pointer items-center justify-center text-gray-500 hover:text-gray-900"
        >
          <CloseSmallIcon className="size-2" />
        </button>
      )}
    </span>
  )
}

function RecipientsField({
  recipients,
  onAdd,
  onRemove,
  placeholder,
  submitKeys = ['Enter', ','],
  removeAriaLabel,
  className,
  inputClassName,
  chipClassName,
}: RecipientsFieldProps) {
  const [draft, setDraft] = React.useState('')
  const submit = () => {
    const v = draft.trim()
    if (!v) return
    onAdd?.(v)
    setDraft('')
  }
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 flex-wrap items-center gap-2',
        className,
      )}
    >
      {recipients.map((r) => (
        <RecipientChip
          key={r.id}
          recipient={r}
          onRemove={onRemove}
          removeAriaLabel={removeAriaLabel}
          className={chipClassName}
        />
      ))}
      {onAdd && (
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (submitKeys.includes(e.key)) {
              e.preventDefault()
              submit()
            }
          }}
          onBlur={submit}
          placeholder={placeholder}
          className={cn(
            'min-w-32 flex-1 bg-transparent text-xs text-gray-900 placeholder:text-gray-400 focus:outline-none',
            inputClassName,
          )}
        />
      )}
    </div>
  )
}

export { RecipientsField, RecipientChip }
export type { RecipientsFieldProps, Recipient }
